import debounce from 'lodash/debounce';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { addWindow, resetWindows, setMobileView } from 'store/slice/pdpStateSlice';
import { FeatureFlags } from 'featureFlags/constants';
import { fireSnapchatPDPView } from 'tracking/utils/snapchatEvents';
import { fireTiktokPDPView } from 'tracking/utils/tiktokEvents';
import { getUtmDataForSalesFloorApp } from 'shared/utils/getUtmDataForSalesFloorApp';
import { IGmcOffers } from 'shared/types/Offer';
import { IProductTemplate } from 'shared/types/ProductTemplate';
import { LOCALES } from 'localization/constants/configEnums';
import { OffersProvider } from 'productTemplate/providers/OffersProvider';
import { ProductDetailPageHead } from 'productTemplate/components/ProductDetailPageHead';
import { requestService } from 'shared/utils/services/requestService';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { SizeConversionProvider } from 'productTemplate/providers/SizeConversionProvider';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useFetchBuyBarVariantsQuery } from 'store/api/buyBarVariantsApi';
import { useFetchProductVariantQuery } from 'store/api/productVariantApi';
import { useLocation } from 'shared/hooks/useLocation';
import { useMParticleContext } from 'tracking/hooks/useMParticleContext';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { useWindowSize } from 'shared/hooks/useWindowSize';

const ProductDetailPageDesktop = dynamic(
  () =>
    import('productTemplate/components/ProductDetailPageDesktop').then(
      (mod) => mod.ProductDetailPageDesktop,
    ),
  { ssr: true },
);
const ProductDetailPageMobile = dynamic(
  () =>
    import('productTemplate/components/ProductDetailPageMobileDeprecated').then(
      (mod) => mod.ProductDetailPageMobileDeprecated,
    ),
  { ssr: true },
);

const ProductTemplateMobileSearchLayout = dynamic(
  () =>
    import('productTemplate/components/ProductTemplateMobileSearchLayout').then(
      (mod) => mod.ProductTemplateMobileSearchLayout,
    ),
  { ssr: true },
);

interface IProductDetailPage {
  isCrawler: boolean;
  locale?: LOCALES;
  offers: IGmcOffers;
  productTemplate: IProductTemplate;
}

const ProductDetailPageLanding = ({
  isCrawler = false,
  offers,
  productTemplate,
  locale,
}: IProductDetailPage) => {
  const isSnapchatEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_SNAPCHAT);
  const isTiktokEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_TIKTOK);
  const { user, clientDedupID } = useStoreSelector((state) => state.auth);
  const { query, asPath, events } = useRouter();
  const storeDispatch = useStoreDispatch();
  const { isMParticleReady } = useMParticleContext();
  const { isMobile, isTablet, isDesktop } = useWindowSize();
  const {
    brandName,
    id,
    isAvailabilityRestricted,
    name: productName,
    slug,
  } = productTemplate || {};

  const [variantIdFromUrl, setVariantIdFromUrl] = useState<string>(null);
  const { data: productVariantData } = useFetchProductVariantQuery(
    { id: variantIdFromUrl },
    { skip: !variantIdFromUrl },
  );
  const { selectedCountryCode } = useLocation();
  const isItemAvailabilityRestricted = isAvailabilityRestricted;
  const { isFetching } = useFetchBuyBarVariantsQuery(
    {
      id,
      selectedCountryCode,
    },
    { skip: isItemAvailabilityRestricted },
  );

  const debouncedSnapchatPDPView = debounce((retry = 5, previousTimeout = null) => {
    const { status: fireSnapchatPDPViewStatus } = fireSnapchatPDPView({
      lowestPriceCents: productTemplate?.lowestPriceCents,
      sku: productTemplate?.sku,
      brandName: productTemplate?.brandName,
      clientDedupID,
    });

    if (previousTimeout) {
      clearTimeout(previousTimeout);
    }

    if (fireSnapchatPDPViewStatus !== 'success' && retry > 0) {
      const timeOutID = setTimeout(() => {
        debouncedSnapchatPDPView(retry - 1, timeOutID);
      }, 1000);
    }
  }, 100);

  const debouncedTiktokPDPView = debounce(() => {
    fireTiktokPDPView(
      {
        contentCategory: productTemplate?.brandName,
        contentId: productTemplate?.sku,
        contentName: productTemplate?.name,
        price: productTemplate?.lowestPriceCents,
      },
      user?.email,
    );
  }, 100);

  useEffect(() => {
    const hash = /#id=((\w|-)+)/.exec(asPath);
    if (query && query.productId) {
      setVariantIdFromUrl(query.productId as string);
    } else if (hash && hash[1]) {
      setVariantIdFromUrl(hash[1]);
    }
  }, [query, asPath]);

  // Show variant window with a delay to display on top of facts window.
  // 60ms delay because the facts window shows up with a 50ms delay.
  useEffect(() => {
    let timer;
    if (productVariantData) {
      timer = setTimeout(() => {
        storeDispatch(addWindow(productVariantData));
        storeDispatch(setMobileView({ view: 'detail', size: productVariantData.size }));
      }, 60);
    }
    return () => clearTimeout(timer);
  }, [productVariantData]);

  useEffect(() => {
    if (isMParticleReady) {
      const currentPath = sessionStorage.getItem('currentPath');

      sendTrackingEvent('PDP_VIEW', {
        product_template_id: id,
        product_template_name: productName,
        product_template_slug: slug,
        content_type: 'product_group',
        reference_source: currentPath,
        brand_name: brandName,
      });

      const utmDataForSalesFloorApp = getUtmDataForSalesFloorApp(query);
      if (utmDataForSalesFloorApp) {
        sendTrackingEvent('SALESFLOOR_PDP_VIEW', utmDataForSalesFloorApp);
      }
    }
  }, [isMParticleReady, id, query]);

  useEffect(() => {
    if (isSnapchatEnabled) {
      debouncedSnapchatPDPView();
    }

    if (isTiktokEnabled) {
      debouncedTiktokPDPView();
    }
  }, [productTemplate?.slug]);

  const handleBeforeUnload = () => {
    storeDispatch(resetWindows());
  };

  useEffect(() => {
    if (!isMobile) {
      events.on('routeChangeStart', handleBeforeUnload);
    }

    return () => {
      events.off('routeChangeStart', handleBeforeUnload);
    };
  }, [isMobile]);

  // measuring page load speed and sending it to Loki
  useEffect(() => {
    const handleLoad = async () => {
      const pageLoaded = performance.mark('pdp-loaded');
      if (pageLoaded) {
        const loadTimeInSeconds = pageLoaded?.startTime / 1000;
        if (loadTimeInSeconds > 0) {
          try {
            await requestService({
              url: '/friendly-logs',
              method: 'POST',
              headers: {
                'x-goat-pdp-view': isMobile ? 'mobile' : 'desktop',
                'x-goat-pdp-load-time': loadTimeInSeconds.toFixed(3),
              },
            });
          } catch {
            // do nothing
          }
        }
      }
    };

    window.addEventListener('load', handleLoad);

    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [isMobile]);

  return (
    <>
      <ProductDetailPageHead
        locale={locale}
        offers={offers}
        productTemplate={productTemplate}
        productType={asPath?.split('/')?.[1]}
      />
      <>
        {isCrawler && (
          <ProductTemplateMobileSearchLayout
            offersData={offers}
            locale={locale}
            isAvailabilityRestricted={isItemAvailabilityRestricted}
          />
        )}
        {!isCrawler && (
          <SizeConversionProvider>
            <OffersProvider>
              {isMobile && (
                <ProductDetailPageMobile
                  isAvailabilityRestricted={isItemAvailabilityRestricted}
                  isFetchingBuyBarData={isFetching}
                />
              )}
              {(isTablet || isDesktop) && (
                <ProductDetailPageDesktop
                  isAvailabilityRestricted={isItemAvailabilityRestricted}
                  isFetchingBuyBarData={isFetching}
                />
              )}
            </OffersProvider>
          </SizeConversionProvider>
        )}
      </>
    </>
  );
};

export default ProductDetailPageLanding;
